import { useCallback, useEffect, useState } from "react";
import { Toaster, toast } from "sonner";
import { api, formatApiError } from "../../lib/apiClient";
import ImageUpload from "../../components/ImageUpload";

const DAYS = [
    ["monday", "Lundi"],
    ["tuesday", "Mardi"],
    ["wednesday", "Mercredi"],
    ["thursday", "Jeudi"],
    ["friday", "Vendredi"],
    ["saturday", "Samedi"],
    ["sunday", "Dimanche"],
];

export default function ShopsManager() {
    const [shops, setShops] = useState([]);

    const fetchAll = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/shops");
            setShops(data);
        } catch (_e) {
            toast.error("Erreur de chargement");
        }
    }, []);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    const updateShop = async (slug, payload) => {
        try {
            await api.put(`/admin/shops/${slug}`, payload);
            toast.success("Boutique mise à jour");
            fetchAll();
        } catch (e2) {
            toast.error(formatApiError(e2.response?.data?.detail) || "Erreur");
        }
    };

    return (
        <div data-testid="admin-shops-page">
            <Toaster richColors position="top-center" />
            <p className="section-tag">administration</p>
            <h1 className="font-serif-display text-4xl text-[#2A1408] tracking-tight">
                Boutiques & horaires
            </h1>

            <div className="grid md:grid-cols-2 gap-6 mt-6">
                {shops.map((s) => (
                    <ShopForm key={s.slug} shop={s} onSubmit={updateShop} />
                ))}
            </div>
        </div>
    );
}

function ShopForm({ shop, onSubmit }) {
    const [form, setForm] = useState(shop);

    useEffect(() => {
        setForm(shop);
    }, [shop]);

    const setHours = (k, v) =>
        setForm((f) => ({ ...f, hours: { ...f.hours, [k]: v } }));

    const handle = async (e) => {
        e.preventDefault();
        await onSubmit(shop.slug, {
            name: form.name,
            address: form.address,
            phone: form.phone,
            email: form.email,
            hours: form.hours,
            image_url: form.image_url,
            map_embed_url: form.map_embed_url,
        });
    };

    return (
        <form
            onSubmit={handle}
            className="brand-card p-6 space-y-3"
            data-testid={`shop-form-${shop.slug}`}
        >
            <h2 className="font-serif-display text-2xl text-[#2A1408]">{shop.name}</h2>
            <label className="block text-xs uppercase tracking-widest text-[#5C3D2E]">
                Nom
                <input
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                />
            </label>
            <label className="block text-xs uppercase tracking-widest text-[#5C3D2E]">
                Adresse
                <input
                    value={form.address}
                    onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                    className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                />
            </label>
            <div className="grid grid-cols-2 gap-3">
                <label className="block text-xs uppercase tracking-widest text-[#5C3D2E]">
                    Téléphone
                    <input
                        value={form.phone}
                        onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                        className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                    />
                </label>
                <label className="block text-xs uppercase tracking-widest text-[#5C3D2E]">
                    Email
                    <input
                        value={form.email}
                        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                        className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                    />
                </label>
            </div>
            <label className="block text-xs uppercase tracking-widest text-[#5C3D2E]">
                Photo de la boutique
                <div className="mt-1">
                    <ImageUpload
                        value={form.image_url}
                        onChange={(url) => setForm((f) => ({ ...f, image_url: url }))}
                        label=""
                    />
                </div>
            </label>
            <label className="block text-xs uppercase tracking-widest text-[#5C3D2E]">
                Carte embed (Google Maps URL)
                <input
                    value={form.map_embed_url}
                    onChange={(e) => setForm((f) => ({ ...f, map_embed_url: e.target.value }))}
                    className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                />
            </label>

            <fieldset className="space-y-2">
                <legend className="text-xs uppercase tracking-widest text-[#5C3D2E]">
                    Horaires
                </legend>
                {DAYS.map(([k, label]) => (
                    <div key={k} className="flex items-center gap-3">
                        <span className="w-24 text-sm text-[#5C3D2E]">{label}</span>
                        <input
                            value={form.hours?.[k] || ""}
                            onChange={(e) => setHours(k, e.target.value)}
                            placeholder="ex: 10h00 – 19h00"
                            className="flex-1 bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                            data-testid={`shop-hours-${shop.slug}-${k}`}
                        />
                    </div>
                ))}
            </fieldset>

            <button type="submit" className="brand-btn" data-testid={`shop-save-${shop.slug}`}>
                Enregistrer
            </button>
        </form>
    );
}
